import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { BasculeMode } from '../components/ui/BasculeMode';
import { usePerimetre } from '../site/usePerimetre';
import { useParcours, type EtapeParcours, type ModeDossier } from '../lib/useParcours';
import { definitionEcran } from '../config/ecrans';
import { useAuth } from '../auth/useAuth';
import { ErreurApi } from '../lib/api';

/**
 * Écran « Parcours candidat / apprenant » (CDC §3).
 *
 * Vue transverse : les deux points d'entrée, les neuf étapes, et ce que le mode
 * change. Chaque étape porte le nombre de dossiers qui s'y trouvent, pour que
 * l'écran serve de point d'entrée de travail et pas seulement de schéma.
 */
export function ParcoursPage(): JSX.Element {
  const { site } = usePerimetre();
  const [mode, setMode] = useState<ModeDossier>('CFA');
  const { data, isLoading, error } = useParcours(site, mode);

  const compteurs = data?.compteurs;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Parcours candidat / apprenant"
        sousTitre="Vue transverse · de l’inscription au suivi post-formation"
        actions={<BasculeMode mode={mode} onChanger={setMode} />}
      />

      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle={`Dossiers en mode ${mode}`}
          valeur={compteurs?.dossiers ?? '—'}
          detail="périmètre affiché"
          ton="navy"
        />
        <Compteur
          libelle="Sur le parcours"
          valeur={compteurs?.surLeParcours ?? '—'}
          detail="répartis sur les étapes ci-dessous"
          ton="blue"
        />
        <Compteur
          libelle="Sans entreprise"
          valeur={compteurs?.sansEntreprise ?? '—'}
          detail="recherche active, ruptures incluses"
          ton="mint"
        />
        <Compteur
          libelle="Hors parcours"
          valeur={compteurs?.horsParcours ?? '—'}
          detail="non retenus et ruptures"
          ton="warm"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 rounded-2xl bg-gradient-to-br from-codialis-navy to-codialis-blue p-4 sm:p-6 text-white lg:grid-cols-2">
        {(data?.pointsEntree ?? []).map((point, index) => (
          <div key={point.titre} className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-codialis-mint">
              Point d’entrée {index + 1}
            </div>
            <div className="font-display font-bold">{point.titre}</div>
            <p className="mt-1 text-sm text-white/70">{point.description}</p>
          </div>
        ))}
      </div>

      <Carte titre="Étapes clés du parcours" sansBordure>
        {isLoading && <Chargement libelle="Chargement du parcours…" />}
        {error && (
          <div className="p-4 sm:p-6">
            <MessageErreur
              message={
                error instanceof ErreurApi
                  ? error.message
                  : 'Le parcours n’a pas pu être chargé.'
              }
            />
          </div>
        )}
        <div className="divide-y divide-gray-100">
          {(data?.etapes ?? []).map((etape) => (
            <LigneEtape key={etape.numero} etape={etape} mode={mode} />
          ))}
        </div>
      </Carte>

      {data && data.horsParcours.length > 0 && (
        <Carte titre="Dossiers hors de la ligne du parcours" sansBordure>
          <div className="divide-y divide-gray-100">
            {data.horsParcours.map((ligne) => (
              <div key={ligne.statut} className="flex items-center gap-3 px-4 sm:px-6 py-3">
                <div className="min-w-0 flex-1 text-sm text-codialis-navy">{ligne.libelle}</div>
                <span className="font-display font-bold text-codialis-navy">
                  {ligne.dossiers}
                </span>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-100 p-4 sm:p-6 text-[11px] text-codialis-gray">
            Une rupture ne referme pas le dossier : si la formation est maintenue, le
            dossier revient à l’étape de recherche d’entreprise.
          </div>
        </Carte>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {(['CFA', 'OF'] as const).map((valeur) => (
          <Carte key={valeur} titre={`Mode ${valeur} · ${valeur === 'CFA' ? 'logique CERFA' : 'session & certificat'}`} sansBordure>
            <div className="space-y-2 p-4 sm:p-6 text-sm">
              {(data?.reglesMode[valeur] ?? []).map((regle) => (
                <div key={regle} className="flex items-start gap-2 text-codialis-gray">
                  <span
                    className={`mt-2 h-1 w-1 shrink-0 rounded-full ${
                      valeur === 'CFA' ? 'bg-codialis-sky' : 'bg-codialis-mint'
                    }`}
                  />
                  {regle}
                </div>
              ))}
            </div>
          </Carte>
        ))}
      </div>
    </div>
  );
}

function LigneEtape({
  etape,
  mode,
}: {
  etape: EtapeParcours;
  mode: ModeDossier;
}): JSX.Element {
  const { peutAcceder } = useAuth();
  const definition = definitionEcran(etape.ecran);
  const accessible = etape.applicable && definition?.livre === true && peutAcceder(etape.ecran);

  const contenu = (
    <>
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-bold ${
          etape.applicable
            ? 'bg-codialis-navy text-white'
            : 'bg-gray-100 text-codialis-gray'
        }`}
      >
        {etape.numero}
      </div>
      {/* La ligne porte un numéro, un intitulé et deux pastilles : en 375 px
          il ne restait que 75 px pour l'intitulé, et les pastilles sortaient du
          cadre. Elles passent sous l'intitulé tant que la place manque. */}
      <div className="min-w-0 flex-1 basis-full sm:basis-auto">
        <div
          className={`text-sm font-semibold ${
            etape.applicable ? 'text-codialis-navy' : 'text-codialis-gray'
          }`}
        >
          {etape.titre}
        </div>
        <div className="text-[11px] text-codialis-gray">{etape.description}</div>
      </div>
      {etape.applicable ? (
        etape.dossiers > 0 && (
          <Pastille texte={`${etape.dossiers} dossier${etape.dossiers > 1 ? 's' : ''}`} ton="info" />
        )
      ) : (
        <Pastille texte={`Sans objet en ${mode}`} ton="neutral" />
      )}
      <Pastille texte={etape.acteur} ton="neutral" />
    </>
  );

  if (!accessible) {
    return (
      <div className="flex flex-wrap items-center gap-2 px-4 py-4 sm:flex-nowrap sm:gap-4 sm:px-6">
        {contenu}
        {etape.applicable && definition?.livre !== true && (
          <span className="shrink-0 text-[11px] text-codialis-gray">À venir</span>
        )}
      </div>
    );
  }

  return (
    <Link
      to={`/${etape.ecran}`}
      className="flex flex-wrap items-center gap-2 px-4 py-4 transition hover:bg-gray-50 sm:flex-nowrap sm:gap-4 sm:px-6"
    >
      {contenu}
      <svg aria-hidden="true"
        className="h-4 w-4 shrink-0 text-codialis-gray"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}
